import {
  DwollaBank,
  PortalBorrowerAchAccountFragment,
  PortalWorkoutPlanFragment,
  RepayAccount,
} from '@willow/graphql-iso/src/portal';
import { subtract } from '@willow/shared-iso';
import { z } from '@willow/shared-web';
import { assertNever, EscrowPaymentHistoryItemId, LoanStatus } from '@willow/types-iso';

export const PaymentType = z.enum([
  'fullPayment',
  'fullPaymentWithShortage',
  'principalPrepayment',
  'escrowDeposit',
  'other',
]);

export type PaymentType = z.infer<typeof PaymentType>;

export const PaymentTypeLabels: Record<PaymentType, string> = {
  fullPayment: 'Full payment due',
  fullPaymentWithShortage: 'Full payment with escrow shortage',
  principalPrepayment: 'Principal Prepayment',
  escrowDeposit: 'Escrow Deposit',
  other: 'Other payment',
};

export type PaymentBank = DwollaBank | RepayAccount | PortalBorrowerAchAccountFragment;

/**
 * Repay disables an account when its ACH debit is returned; only the servicer can unfreeze it.
 * Dwolla accounts are removed / don't have the same disabled state
 * Usio accounts are lender-side only and don't show up in the portal UI
 */
export const isBankAccountLocked = (bank: PaymentBank | undefined): boolean =>
  bank?.__typename === 'RepayAccount' && bank.disabled;

export const LOCKED_BANK_ERROR = 'This bank account is locked. Connect a new bank account or contact your bank.';

export const PaymentFormSchema = z
  .object({
    paymentType: PaymentType,
    amount: z.coerce.number().optional(),
    fullPaymentAmount: z.coerce.number(),
    fullPaymentWithShortageAmount: z.coerce.number(),
    paymentBank: z.custom<PaymentBank>().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.paymentType === 'fullPayment' && data.fullPaymentAmount <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['fullPaymentAmount'],
        message: 'Full payment amount must be greater than $0.00',
      });
    }
    if (data.paymentType === 'fullPaymentWithShortage' && data.fullPaymentWithShortageAmount <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['fullPaymentWithShortageAmount'],
        message: 'Full payment with shortage amount must be greater than $0.00',
      });
    }
    if (
      (data.paymentType === 'other' ||
        data.paymentType === 'principalPrepayment' ||
        data.paymentType === 'escrowDeposit') &&
      (data.amount === undefined || data.amount <= 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'Amount must be greater than $0.00',
      });
    }
    if (!data.paymentBank) {
      ctx.addIssue({
        code: 'custom',
        path: ['paymentBank'],
        message: 'Please select a bank account',
      });
    }
  });

export type TPaymentFormSchema = z.infer<typeof PaymentFormSchema>;

export interface PaymentTypeOption {
  value: PaymentType;
  label: string;
  submitDisabled: boolean;
  disabledReason: string;
  extraInfo: string;
}

export interface PaymentTypeOptionsInput {
  fullPaymentAmount: number;
  totalStatementAmountDue: number;
  totalLateFeesDue: number;
  exactPaymentOnly: boolean;
  additionalEscrowShortage: number;
  escrowShortageAnalysisId: EscrowPaymentHistoryItemId | undefined;
  escrowBalance: number;
  loanStatus: LoanStatus;
  interestDueAtPayoff?: boolean;
}

export function getPaymentTypeOptions({
  fullPaymentAmount,
  totalStatementAmountDue,
  totalLateFeesDue,
  exactPaymentOnly,
  additionalEscrowShortage,
  escrowShortageAnalysisId,
  escrowBalance,
  loanStatus,
  interestDueAtPayoff = false,
}: PaymentTypeOptionsInput): PaymentTypeOption[] {
  // Full payment with shortage and escrow deposit are very occasional use, so we hide them from the list.
  // Principal prepayment and other payment happen more often, so we need to tell the user why they can't use them.
  // Escrow deposit displays when there is a negative escrow balance. We disable with the same logic as principal prepayments (ie outstanding statements and fees must be paid first)
  // When exact payment is on, don't allow principal prepayment or other payment.
  return Object.entries(PaymentTypeLabels)
    .filter(
      ([value]) =>
        // An interest-due-at-payoff loan bills nothing, so a voluntary "Other" payment is the only meaningful option
        !(interestDueAtPayoff && value !== 'other') &&
        !(value === 'fullPaymentWithShortage' && (additionalEscrowShortage <= 0 || !escrowShortageAnalysisId)) &&
        !(value === 'escrowDeposit' && (exactPaymentOnly || escrowBalance >= 0)) &&
        // Exact payment holds the borrower to the billed amount, which an interest-due-at-payoff loan does not have
        !(value === 'other' && exactPaymentOnly && !interestDueAtPayoff) &&
        !(value === 'principalPrepayment' && (exactPaymentOnly || loanStatus === 'pendingTransfer')),
    )
    .map(([value, label]) => ({
      value: value as PaymentType,
      label,
      submitDisabled:
        (value === 'fullPayment' && fullPaymentAmount <= 0) ||
        (['principalPrepayment', 'escrowDeposit'].includes(value) &&
          (totalStatementAmountDue > 0 || totalLateFeesDue > 0)),
      disabledReason:
        value === 'fullPayment' && fullPaymentAmount <= 0
          ? 'No required amount due to pay'
          : ['principalPrepayment', 'escrowDeposit'].includes(value) &&
              (totalStatementAmountDue > 0 || totalLateFeesDue > 0)
            ? 'Outstanding statement(s) and applicable late fees must be paid in full'
            : '',
      extraInfo:
        value === 'fullPayment' && additionalEscrowShortage > 0
          ? 'Select "Full payment due with escrow shortage" to pay your calculated shortage now'
          : '',
    }));
}

export function getPaymentTypeDescription(paymentType: PaymentType): string {
  switch (paymentType) {
    case 'fullPayment':
      return '';
    case 'fullPaymentWithShortage':
      return 'Payment will be applied to the full amount due, including any escrow shortage.';
    case 'principalPrepayment':
      return 'Payment will reduce principal balance but may not reduce statement payments.';
    case 'escrowDeposit':
      return 'Payment will be deposited to escrow balance.';
    case 'other':
      return 'Payment will be applied according to the terms of your loan agreement.';
    default:
      assertNever(paymentType);
  }
}

export interface PaymentAllocationInput {
  paymentType: PaymentType;
  amount: number | undefined;
  escrowShortageAnalysisId: EscrowPaymentHistoryItemId | undefined;
  additionalEscrowShortage: number;
  totalAmountDue: number;
  totalUpcomingAmountDue: number;
  totalFutureAmountDue: number;
  totalAmountDueToReinstateLoan: number;
  fullPaymentAmount: number;
  fullPaymentWithShortageAmount: number;
  activeWorkoutPlan: PortalWorkoutPlanFragment | undefined;
}

export interface PaymentAllocationResult {
  paymentAmount: number;
  additionalPrincipal: number;
  additionalEscrow: number;
  additionalSuspense: number;
  escrowShortageAnalysisId?: EscrowPaymentHistoryItemId;
}

export function getPaymentAllocations({
  paymentType,
  amount,
  escrowShortageAnalysisId,
  additionalEscrowShortage,
  totalAmountDue,
  totalFutureAmountDue,
  totalAmountDueToReinstateLoan,
  fullPaymentAmount,
  fullPaymentWithShortageAmount,
  activeWorkoutPlan,
}: PaymentAllocationInput): PaymentAllocationResult {
  let additionalPrincipal = 0;
  let additionalEscrow = 0;
  let additionalSuspense = 0;
  let paymentAmount = 0;
  // When there's an active workout plan, use the actual amount due to reinstate the loan
  // for determining overpayment allocations (not the workout plan reduced amount)
  const amountDueForAllocations = activeWorkoutPlan ? totalAmountDueToReinstateLoan : totalAmountDue;

  switch (paymentType) {
    case 'fullPayment':
      paymentAmount = fullPaymentAmount;
      break;
    case 'fullPaymentWithShortage':
      paymentAmount = fullPaymentWithShortageAmount;
      // For escrow shortage payments, allocate the additional 11/12ths to escrow
      if (escrowShortageAnalysisId) {
        additionalEscrow = additionalEscrowShortage;
      }
      break;
    case 'principalPrepayment':
      paymentAmount = +(amount ?? 0);
      additionalPrincipal = paymentAmount;
      break;
    case 'escrowDeposit':
      paymentAmount = +(amount ?? 0);
      additionalEscrow = paymentAmount;
      break;
    case 'other': {
      paymentAmount = +(amount ?? 0);
      // Calculate additional suspense for "Other" payment type
      // Use workout plan-aware amount for determining overpayment
      const hasCurrentAmountDue = amountDueForAllocations > 0;
      const statementAmount = hasCurrentAmountDue ? amountDueForAllocations : totalFutureAmountDue;
      let calculatedSuspense = 0;

      if (totalFutureAmountDue > statementAmount && paymentAmount > totalFutureAmountDue) {
        // If there's an upcoming statement payment, account for that when calculating additional suspense
        // This will happen between the 18th and EOM when other payment is greater amount due
        calculatedSuspense = subtract(paymentAmount, totalFutureAmountDue);
      } else {
        calculatedSuspense = subtract(paymentAmount, statementAmount);
      }
      additionalSuspense = calculatedSuspense < 0 ? 0 : calculatedSuspense;
      break;
    }
    default:
      assertNever(paymentType);
  }

  return {
    paymentAmount,
    additionalPrincipal,
    additionalEscrow,
    additionalSuspense,
    // Include escrowShortageAnalysisId when paying with shortage upfront
    ...(paymentType === 'fullPaymentWithShortage' && escrowShortageAnalysisId ? { escrowShortageAnalysisId } : {}),
  };
}
